/**
 * Floorplan Transformation Utilities
 * ===================================
 * Centralized transformation logic for floorplan names, amenities, and deals
 * 
 * ## Overview
 * These utilities handle all the property-specific naming conventions and
 * data corrections needed across the application. The main focus is on
 * transforming floorplan descriptors (bedroom/bathroom counts and square footage)
 * and appending or modifying floorplan names based on property-specific rules.
 * 
 * NOTE: The API field 'amenities' is a misnomer - it actually contains
 * floorplan descriptors like "2bd x 1ba - 900sf" rather than true amenities.
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage (floorplan descriptors only):
 * ```javascript
 * import { transformUnits } from '../utils/floorplanTransforms';
 * 
 * // Transform floorplan descriptor field (mislabeled as 'amenities' in API)
 * const transformed = transformUnits(units, yardiCid);
 * ```
 * 
 * ### Full Usage (descriptors + floorplan names):
 * ```javascript
 * import { transformUnits } from '../utils/floorplanTransforms';
 * 
 * // Transform both floorplan descriptor and floorplanName fields
 * const transformed = transformUnits(units, yardiCid, { 
 *   updateFloorplanName: true,
 *   cid: communityId 
 * });
 * ```
 * 
 * ### Deal Transformations:
 * ```javascript
 * import { transformDeals } from '../utils/floorplanTransforms';
 * 
 * // Fix deal descriptions
 * const fixedDeals = transformDeals(deals, communityId);
 * ```
 * 
 * ## Important Notes
 * - The 'amenities' field in the API is mislabeled - it contains floorplan descriptors
 *   (bedroom/bathroom counts and square footage), not actual amenities
 * - Transformations are applied IN PLACE (units are mutated)
 * - Always returns an array (empty array if input is invalid)
 * - Order matters: CID-specific transforms run before Yardi-specific
 * - Some properties have special logic (e.g., unit swapping for McDonald)
 */

/**
 * Transform deal descriptions to fix common data issues
 * 
 * @param {Array} deals - Array of deal objects with properties:
 *   - sdesc: Short description of the deal
 *   - unit: Unit code
 * @param {string|number} cid - Community ID (rmpropid)
 * 
 * @returns {Array} Transformed deals array
 * 
 * @example
 * const deals = [
 *   { sdesc: "2bd x 1ba - 900sf", unit: "123", ... },
 *   { sdesc: "College", unit: "456", ... }
 * ];
 * const fixed = transformDeals(deals, "401520");
 * // Result: [
 * //   { sdesc: "2bd x 1ba - 900sf - Boca", ... },
 * //   { sdesc: "Harmony", ... }
 * // ]
 */
export function transformDeals(deals, cid) {
  if (!deals || !Array.isArray(deals)) return [];
  
  return deals.map(r => {
    // Beach Flats specific fixes
    if (cid == 401520) {
      if (r.sdesc === '2bd x 2ba - 1034sf - Captiva') {
        r.sdesc = '2bd x 2ba - 1034sf';
      }
      if (r.sdesc === '2bd x 1ba - 900sf') {
        r.sdesc = '2bd x 1ba - 900sf - Boca';
      }
    }
    
    // Global fixes for all properties
    if (r.sdesc == "2bd x 1.5ba - 1") {
      r.sdesc = "2bd x 1.5ba - 1000sf - Chatham";
    }
    if (r.sdesc.includes("bagley")) {
      r.sdesc = r.sdesc.replace("bagley", "Bagley");
    }
    if (r.sdesc.includes("barber")) {
      r.sdesc = r.sdesc.replace("barber", "Barber");
    }
    if (r.sdesc.includes("College")) {
      r.sdesc = r.sdesc.replace("College", "Harmony");
    }
    if (r.sdesc.includes("barton")) {
      r.sdesc = r.sdesc.replace("barton", "Barton");
    }
    if (r.sdesc.includes("Punta Carena Market")) {
      r.sdesc = r.sdesc.replace("Punta Carena Market", "Punta Carena");
    }
    if (r.sdesc.includes("Angell")) {
      r.sdesc = r.sdesc.replace("Angell", "Bryant");
    }
    if (r.sdesc.includes("Burns Park")) {
      r.sdesc = r.sdesc.replace("Burns Park", "Clague");
    }
    if (r.sdesc.includes("Parc")) {
      r.sdesc = r.sdesc.replace("Parc", "The Conroy");
    }
    if (r.unit.includes("07-1844")) {
      r.sdesc = r.sdesc.replace("The Starling", "Blue Heron");
    }
    
    return r;
  });
}

/**
 * Transform units - handles floorplan descriptors and floorplan names
 * 
 * @param {Array} units - Array of unit objects with properties:
 *   - amenities: Floorplan descriptor string (e.g., "2bd x 1ba - 900sf")
 *                ⚠️ MISLABELED: This API field name is 'amenities' but it actually contains
 *                the floorplan descriptor (bedroom/bathroom count and square footage)
 *   - floorplanName: (optional) Name of the floorplan
 *   - unit: Unit code/number
 *   - position/extra: (optional) Additional unit info (e.g., "Top", "renovated")
 * 
 * @param {string} yardiCid - Yardi property ID (e.g., "beachfl1", "willfla1")
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.updateFloorplanName - If true, also updates floorplanName field
 * @param {string|number} options.cid - Community ID for CID-specific transformations
 * 
 * @returns {Array} Transformed units array (same reference, mutated in place)
 * 
 * @example
 * // Basic usage - transform floorplan descriptors only
 * const units = [
 *   { amenities: "2bd x 1ba", unit: "101" },  // 'amenities' is actually the floorplan descriptor
 *   { amenities: "Studio x 1ba", unit: "102" }
 * ];
 * transformUnits(units, "willfla1");
 * // Result: units[0].amenities = "2bd x 1ba - Morning Glory"
 * 
 * @example
 * // Full usage - transform both floorplan descriptors and floorplan names
 * const units = [
 *   { amenities: "2bd x 1ba", floorplanName: "2bd", unit: "101" }
 * ];
 * transformUnits(units, "willfla1", { 
 *   updateFloorplanName: true,
 *   cid: "401830" 
 * });
 * // Result: 
 * //   units[0].amenities = "2bd x 1ba - Morning Glory"  // descriptor with floorplan name appended
 * //   units[0].floorplanName = "Morning Glory"
 * 
 * ## Property-Specific Transformations
 * 
 * ### Special Cases:
 * - **brooklyn**: Duplicates units with Henderson → Darcy
 * - **mcdonald**: Swaps amenities between units 2175-05 and 2175-07
 * - **capri**: Adds "+" for renovated/upgraded units based on position
 * - **meadow**: Changes names based on floor (Top → Cardinal, Ground → Blue Heron)
 * 
 * ### Common Patterns:
 * - Plus/Signature → "+" standardization
 * - Capitalization fixes (barber → Barber, bagley → Bagley)
 * - College → Harmony renaming
 * - Angell → Bryant, Burns Park → Clague
 */
export function transformUnits(units, yardiCid, options = {}) {
  if (!units || !Array.isArray(units)) return [];
  
  const { updateFloorplanName = false, cid = null } = options;
  let a = [...units];
  
  // ⚠️ IMPORTANT: The 'amenities' field from the API is mislabeled!
  // It actually contains floorplan descriptors like "2bd x 1ba - 900sf", not amenities.
  // We keep the field name to match the API, but it represents bedroom/bath/sqft info.
  
  // Apply CID-specific transformations first (these only affect floorplanName)
  if (updateFloorplanName && cid) {
    if (cid == "401910") {
      a.forEach((n) => {
        switch (n.floorplanName) {
          case "700sf ":
            n.floorplanName = "Iris Meadow";
            break;
          case "570sf ":
            n.floorplanName = "Purple Milkweed";
            break;
        }
      });
    } else if (cid == "401810") {
      a.forEach((n) => {
        n.floorplanName = "West Mesa";
      });
    } else if (cid == "400410") {
      a.forEach((n) => {
        if (n.floorplanName === "Parc") {
          n.floorplanName = "The Conroy";
          n.amenities = n.amenities.replace("Parc", "The Conroy");
        }
      });
    }
  }
  
  switch(yardiCid) {
    case "willfla1":
      a.forEach(n => {
        // n.amenities is the floorplan descriptor from API (e.g., "2bd x 1ba")
        // We append the floorplan name to create the full descriptor
        if (n.amenities.includes("Sbd x 1ba")) {
          n.amenities += " - Aloe";  // Descriptor: "Sbd x 1ba - Aloe"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Aloe";
        }
        if (n.amenities.includes("2bd x 1ba")) {
          n.amenities += " - Morning Glory";  // Descriptor: "2bd x 1ba - Morning Glory"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Morning Glory";
        }
        if (n.amenities.includes("1bd x 1ba")) {
          n.amenities += " - Canary";  // Descriptor: "1bd x 1ba - Canary"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Canary";
        }
        if (n.amenities.includes("2bd x 2ba")) {
          n.amenities += " - Gardenia";  // Descriptor: "2bd x 2ba - Gardenia"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Gardenia";
        }
      });
      break;
      
    case "schoofla":
      a.forEach(n => {
        // Match on floorplan descriptor (beds/baths/sqft) - NOT amenities!
        switch (n.amenities) {
          case "1bd x 1ba - 700sf ":
            n.amenities = n.amenities + " - Iris Meadow";
            if (updateFloorplanName && n.floorplanName) n.floorplanName = "Iris Meadow";
            break;
          case "Sbd x 1ba - 570sf ":
            n.amenities = n.amenities + " - Purple Milkweed";
            if (updateFloorplanName && n.floorplanName) n.floorplanName = "Purple Milkweed";
            break;
        }
      });
      break;
      
    case "olivia":
      a.forEach(n => {
        // Append floorplan name to all descriptors for this property
        n.amenities = n.amenities + " - West Mesa";  // e.g., "2bd x 1ba - 900sf - West Mesa"
        if (updateFloorplanName && n.floorplanName) n.floorplanName = "West Mesa";
      });
      break;
      
    case "beachfl3":
      a.forEach(n => {
        // Transform specific floorplan descriptor
        if (n.amenities.includes("2bd x 1ba - 900sf")) {
          n.amenities += " - Boca";  // Descriptor: "2bd x 1ba - 900sf - Boca"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Boca";
        }
      });
      break;
      
    case "beachfl1":
      a.forEach(n => {
        // n.amenities contains floorplan descriptor like "2bd x 1ba - 900sf"
        if (n.amenities.includes("2bd x 1ba - 900sf")) {
          n.amenities += " - Boca";  // Becomes: "2bd x 1ba - 900sf - Boca"
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Boca";
        }
        if (n.amenities.includes("2bd x 2ba")) {
          n.amenities += " - Warwick Long Bay";
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Warwick Long Bay";
        }
        if (n.amenities.includes("1bd x 1ba")) {
          n.amenities += " - Sanibel";
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Sanibel";
        }
        if (n.amenities.includes("Studio x 1ba")) {
          n.amenities += " - Captiva";
          if (updateFloorplanName && n.floorplanName) n.floorplanName = "Captiva";
        }
        if (updateFloorplanName && n.amenities.includes("Pensacola")) {
          n.floorplanName = "Pensacola";
        }
      });
      break;
      
    case "sc1":
      a.forEach(n => {
        if (n.amenities == "2bd x 1ba - 950sf - The Grand" || 
            n.amenities == "2bd x 1ba - 950sf - The Gaston") {
          n.amenities = "2bd x 1ba - 950sf - The Gaston & The Grand";
          if (updateFloorplanName && n.floorplanName) {
            n.floorplanName = "The Gaston & The Grand";
          }
        }
      });
      break;
      
    case "spruce":
      a.forEach(n => {
        if (n.amenities == "2bd x 1ba - 812sf - The basquiat") {
          n.amenities = "2bd x 1ba - 812sf - The Basquiat";
          if (updateFloorplanName && n.floorplanName) {
            n.floorplanName = "The Basquiat";
          }
        }
        if (updateFloorplanName && n.floorplanName?.includes("basquiat")) {
          n.floorplanName = n.floorplanName.replace("basquiat", "Basquiat");
        }
      });
      break;
      
    case "statestr":
      a.forEach(n => {
        if (n.amenities.includes("Jefferson TH")) {
          n.amenities = n.amenities.replace(/\s+TH$/, "");
        } else if (n.amenities.includes("Summit")) {
          n.amenities = n.amenities.replace("Summit", "Summit & Thompson");
        } else if (n.amenities.includes("Thompson")) {
          n.amenities = n.amenities.replace("Thompson", "Summit & Thompson");
        } else if (n.amenities.includes("McKinley")) {
          n.amenities = n.amenities.replace("McKinley", "The McKinley");
        }
      });
      break;
      
    case "brooklyn":
      // Special case: duplicate units with Henderson -> Darcy
      // The 'amenities' field contains the floorplan descriptor (e.g., "2bd x 1ba - The Henderson")
      let b = a.map(item => ({
        ...item,
        amenities: item.amenities.replace("The Henderson", "The Darcy"),  // Transform descriptor
        ...(updateFloorplanName && item.floorplanName && {
          floorplanName: item.floorplanName.replace("The Henderson", "The Darcy")
        })
      }));
      a = a.concat(b);
      break;
      
    case "capri":
      a.forEach(n => {
        // Check if floorplan descriptor contains "Punta Carena"
        if (n.amenities.includes("Punta Carena")) {
          const pos = (n.position || n.extra || '').toLowerCase();
          if (pos.includes("renovated") || pos.includes("upgraded")) {
            // Add + to descriptor for renovated units
            n.amenities = n.amenities.replace("Punta Carena", "Punta Carena+");
            if (updateFloorplanName && n.floorplanName) {
              n.floorplanName = n.floorplanName.replace("Punta Carena", "Punta Carena+");
            }
          }
        }
      });
      break;
      
    case "coconut":
      a.forEach(n => {
        if (n.amenities.includes("Estereo bay")) {
          n.amenities = n.amenities.replace("Estereo bay", "Estereo Bay");
          if (updateFloorplanName && n.floorplanName == "Estereo bay") {
            n.floorplanName = "Estereo Bay";
          }
        }
        if (n.amenities.includes("Crystal River")) {
          n.amenities = n.amenities.replace("Crystal River", "Alafia River");
          if (updateFloorplanName && n.floorplanName == "Crystal River") {
            n.floorplanName = "Alafia River";
          }
        }
        if (n.amenities.includes("Plus")) {
          n.amenities = n.amenities.replace(" Plus", "+");
          if (updateFloorplanName && n.floorplanName?.includes("Plus")) {
            n.floorplanName = n.floorplanName.replace(" Plus", "+");
          }
        }
      });
      break;
      
    case "harbor":
      a.forEach(n => {
        if (n.amenities.includes("barber")) {
          n.amenities = n.amenities.replace("barber", "Barber");
          if (updateFloorplanName && n.floorplanName == "barber") {
            n.floorplanName = "Barber";
          }
        }
      });
      break;
      
    case "indigo":
      a.forEach(n => {
        if (n.amenities.includes("College")) {
          n.amenities = n.amenities.replace("College", "Harmony");
          if (updateFloorplanName && n.floorplanName === "College") {
            n.floorplanName = "Harmony";
          }
        }
      });
      break;
      
    case "mcdonald":
      a.forEach(n => {
        // Replace floorplan name within the descriptor
        if (n.amenities.includes("Birch")) {
          n.amenities = n.amenities.replace("Birch", "Carriage House");
          if (updateFloorplanName && n.floorplanName == "Birch") {
            n.floorplanName = "Carriage House";
          }
        }
      });
      
      // Special swapping logic for specific units
      let unit_05 = null;
      let unit_07 = null;
      a.forEach(n => {
        if (n.unit === "2175-05") unit_05 = n;
        else if (n.unit === "2175-07") unit_07 = n;
      });
      if (unit_05 && unit_07) {
        // Swap the floorplan descriptors between these two units
        const tempDescriptor = unit_05.amenities;  // Actually the floorplan descriptor
        unit_05.amenities = unit_07.amenities;
        unit_07.amenities = tempDescriptor;
        
        if (updateFloorplanName && unit_05.floorplanName && unit_07.floorplanName) {
          const tempFloorplanName = unit_05.floorplanName;
          unit_05.floorplanName = unit_07.floorplanName;
          unit_07.floorplanName = tempFloorplanName;
        }
      }
      break;
      
    case "tracross":
      a.forEach(n => {
        if (n.amenities.includes("bagley")) {
          n.amenities = n.amenities.replace("bagley", "Bagley");
          if (updateFloorplanName && n.floorplanName === "The bagley") {
            n.floorplanName = "The Bagley";
          }
        }
      });
      break;
      
    case "sapphire":
      a.forEach(n => {
        if (n.amenities.includes("Plus")) {
          n.amenities = n.amenities.replace(" Plus", "+");
        }
        if (n.amenities.includes("Signature")) {
          n.amenities = n.amenities.replace(" Signature", "+");
        }
        if (updateFloorplanName && n.floorplanName) {
          if (n.floorplanName.includes("Plus")) {
            n.floorplanName = n.floorplanName.replace(" Plus", "+");
          }
          if (n.floorplanName.includes("Signature")) {
            n.floorplanName = n.floorplanName.replace(" Signature", "+");
          }
        }
      });
      break;
      
    case "manflats":
      a.forEach(n => {
        if (n.unit?.trim() == "1820-01") {
          n.amenities = n.amenities.replace("Burlington", "Fletcher");
          if (updateFloorplanName && n.floorplanName) {
            n.floorplanName = "The Fletcher";
          }
        }
      });
      break;
      
    case "medical":
      a.forEach(n => {
        if (n.amenities.includes("The barton Hills")) {
          n.amenities = n.amenities.replace("The barton Hills", "The Barton Hills");
        }
        if (updateFloorplanName && n.floorplanName?.includes("barton")) {
          n.floorplanName = n.floorplanName.replace("barton", "Barton");
        }
      });
      break;
      
    case "tracourt":
    case "westwood":
    case "azure":
      a.forEach(n => {
        if (n.amenities.includes("Plus")) {
          n.amenities = n.amenities.replace(" Plus", "+");
          if (updateFloorplanName && n.floorplanName?.includes("Plus")) {
            n.floorplanName = n.floorplanName.replace(" Plus", "+");
          }
        }
      });
      break;
      
    case "evergree":
      a.forEach(n => {
        if (n.amenities.includes("Angell")) {
          n.amenities = n.amenities.replace("Angell", "Bryant");
          if (updateFloorplanName && n.floorplanName == "Angell") {
            n.floorplanName = "Bryant";
          }
        }
        if (n.amenities.includes("Burns Park")) {
          n.amenities = n.amenities.replace("Burns Park", "Clague");
          if (updateFloorplanName && n.floorplanName == "Burns Park") {
            n.floorplanName = "Clague";
          }
        }
        if (n.amenities.includes("Kerrytown")) {
          n.amenities = n.amenities.replace("Kerrytown", "Georgetown");
          if (updateFloorplanName && n.floorplanName == "Kerrytown") {
            n.floorplanName = "Georgetown";
          }
        }
      });
      break;
      
    case "meadow":
      a.forEach(n => {
        // Transform floorplan name in descriptor based on unit position
        if (n.amenities.includes("The Meadowlark") && 
            (n.position || n.extra || '').includes("Top")) {
          n.amenities = n.amenities.replace("Meadowlark", "Cardinal");  // Top floor gets different name
          if (updateFloorplanName && n.floorplanName == "The Meadowlark") {
            n.floorplanName = "The Cardinal";
          }
        }
        if (n.amenities.includes("The Starling") && 
            (n.position || n.extra || '').includes("Ground")) {
          n.amenities = n.amenities.replace("The Starling", "Blue Heron");
          if (updateFloorplanName && n.floorplanName == "The Starling") {
            n.floorplanName = "Blue Heron";
          }
        }
        if (n.amenities.includes("The barton Hills")) {
          n.amenities = n.amenities.replace("The barton Hills", "The Barton Hills");
        }
      });
      break;
  }
  
  return a;
}

/**
 * Extract floorplan name from floorplan descriptor string
 * 
 * @param {string} str - Floorplan descriptor string (mislabeled as 'amenities' in API)
 *                      Format: "[beds] x [baths] - [sqft] - [floorplan name]"
 *                      Example: "2bd x 1ba - 900sf - Boca"
 * @returns {string} The floorplan name (last part after final dash)
 * 
 * @example
 * getFloorplanNameYardi("2bd x 1ba - 900sf - Boca") // Returns: "Boca"
 * getFloorplanNameYardi("Studio x 1ba - 570sf - Captiva") // Returns: "Captiva"
 * getFloorplanNameYardi("2bd x 2ba - 1034sf") // Returns: "1034sf"
 */
export function getFloorplanNameYardi(str) {
  // Extract floorplan name from descriptor string
  // Input format: "2bd x 1ba - 900sf - Boca" where 'str' is the mislabeled 'amenities' field
  if (!str) return '';
  const parts = str.split("-");
  return parts[parts.length - 1].trim();
}

/**
 * Backward compatibility wrapper for floorplanTemplate.js
 * @deprecated Use transformUnits with options instead
 * 
 * @example
 * // Old way (still works but deprecated):
 * transformFloorplanNames(units, yardiCid, cid);
 * 
 * // New way (recommended):
 * transformUnits(units, yardiCid, { updateFloorplanName: true, cid });
 */
export function transformFloorplanNames(units, yardiCid, cid) {
  return transformUnits(units, yardiCid, { updateFloorplanName: true, cid });
}

/**
 * ## Quick Reference Guide
 * 
 * ### Which function to use?
 * 
 * | Use Case | Function | Example |
 * |----------|----------|---------|
 * | Fix deal descriptions | `transformDeals()` | `transformDeals(deals, cid)` |
 * | Transform floorplan descriptors only | `transformUnits()` | `transformUnits(units, yardiCid)` |
 * | Transform descriptors + floorplan names | `transformUnits()` | `transformUnits(units, yardiCid, { updateFloorplanName: true, cid })` |
 * | Extract floorplan name from descriptor | `getFloorplanNameYardi()` | `getFloorplanNameYardi(descriptor)` |
 * 
 * ### Common Property IDs (yardiCid):
 * - beachfl1, beachfl2, beachfl3 - Beach Flats
 * - willfla1, willfla2 - Willow Flats
 * - sc1, sc2, sc3 - South Campus
 * - mcdonald, gleno2 - McDonald/Glenwood
 * - capri - Capri (checks for renovated units)
 * - meadow - Meadow Park (position-based)
 * - brooklyn - Brooklyn (duplicates Henderson as Darcy)
 * 
 * ### Debug Tips:
 * 1. Check if yardiCid matches exactly (case-sensitive)
 * 2. For floorplanName updates, ensure updateFloorplanName: true
 * 3. CID is required for certain special transformations
 * 4. Units are mutated in place - make a copy if you need originals
 */